import React, { useState } from 'react';

import { MenuItem, Select, Typography } from '@mui/material';
import { ConfirmationDialog } from 'src/components/generic';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import type { Column } from './helpers';
import { getColumnDeleteTargets, getSigningColumnDeleteTargetId } from './helpers';

const useStyles = makeStyles()(theme => ({
  select: {
    marginTop: theme.spacing(2),
  },
  selectLabel: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(13),
    marginBottom: theme.spacing(0.5),
  },
}));

interface DeleteColumnDialogProps {
  /** All board columns (the deleted column will be filtered out internally) */
  allColumns: Column[];
  /** The column being deleted */
  column: Column;
  onClose: () => void;
  onConfirm: (columnId: UUID, targetColumnId: UUID) => void;
  /** Board's Signing singleton, which only ever holds signing work */
  signingColumnId?: UUID | null;
}

/**
 * Confirmation dialog for deleting a column.
 * Signing has a dedicated confirmation because its destination is board-managed; every other
 * column, Triage included, asks where its workflows should move first.
 *
 * Rendered conditionally by the parent — use `key={column.id}` so React
 * resets internal state when a different column is selected for deletion.
 */
export function DeleteColumnDialog({
  allColumns,
  column,
  onClose,
  onConfirm,
  signingColumnId,
}: DeleteColumnDialogProps): JSX.Element {
  if (column.id === signingColumnId) {
    return (
      <SigningDeleteColumnDialog
        allColumns={allColumns}
        column={column}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    );
  }

  return (
    <StandardDeleteColumnDialog
      allColumns={allColumns}
      column={column}
      signingColumnId={signingColumnId}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
}

function SigningDeleteColumnDialog({
  allColumns,
  column,
  onClose,
  onConfirm,
}: Omit<DeleteColumnDialogProps, 'signingColumnId'>): JSX.Element {
  const targetColumnId = getSigningColumnDeleteTargetId(allColumns, column.id);

  return (
    <ConfirmationDialog
      open
      message={`Are you sure you want to delete the "${column.name}" column? It can only be deleted when no workflows on this board include signing phases, and it will be recreated automatically if a signing phase is added later.`}
      primaryDisabledReason={
        !targetColumnId ? 'A non-signing Started column is required to delete Signing' : ''
      }
      primaryText="Delete Column"
      title="Delete Column"
      onClickPrimary={async () => {
        if (targetColumnId) await onConfirm(column.id, targetColumnId);
        onClose();
      }}
      onClose={onClose}
    />
  );
}

function StandardDeleteColumnDialog({
  allColumns,
  column,
  onClose,
  onConfirm,
  signingColumnId,
}: DeleteColumnDialogProps): JSX.Element {
  const { classes } = useStyles();
  const moveTargetColumns = getColumnDeleteTargets({ allColumns, column, signingColumnId });
  const [targetColumnId, setTargetColumnId] = useState(moveTargetColumns.at(0)?.id ?? '');
  const targetColumn = moveTargetColumns.find(moveTarget => moveTarget.id === targetColumnId);

  return (
    <ConfirmationDialog
      open
      message={getDeleteMessage(column, targetColumn)}
      primaryDisabledReason={!targetColumnId ? 'Select a column to move workflows to' : ''}
      primaryText="Delete Column"
      title="Delete Column"
      onClickPrimary={async () => {
        if (targetColumnId) {
          await onConfirm(column.id, targetColumnId);
        }
        onClose();
      }}
      onClose={onClose}
    >
      <div className={classes.select}>
        <Typography className={classes.selectLabel}>Move workflows to:</Typography>
        <Select
          displayEmpty
          fullWidth
          size="small"
          value={targetColumnId}
          onChange={e => setTargetColumnId(String(e.target.value))}
        >
          {moveTargetColumns.map(col => (
            <MenuItem key={col.id} value={col.id}>
              {col.name}
            </MenuItem>
          ))}
        </Select>
      </div>
    </ConfirmationDialog>
  );
}

/**
 * The delete moves cards without starting them, so a workflow that has not begun yet lands in a
 * Started column still unstarted, and cannot be dragged on until someone starts it. Say so up
 * front rather than letting the owner hit the guard later.
 */
function getDeleteMessage(column: Column, targetColumn: Column | undefined): string {
  const message = `Are you sure you want to delete the "${column.name}" column? All workflows in this column will be moved to the column you select below.`;

  const landsUnstartedInStarted =
    column.category !== Apollo.ColumnCategory.Started &&
    targetColumn?.category === Apollo.ColumnCategory.Started;
  if (!landsUnstartedInStarted) return message;

  return `${message} Workflows that have not started yet will arrive unstarted and must be manually started before they can be moved again.`;
}
